import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as HiIcons from 'react-icons/hi';
import * as BsIcons from 'react-icons/bs';
import SafeIcon from '../../common/SafeIcon';

const { FiCheck, FiPlay, FiShield, FiZap, FiHeart, FiClock, FiAward, FiEdit3, FiHeadphones } = FiIcons;
const { HiSparkles } = HiIcons;
const { BsBook, BsMic } = BsIcons;

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const benefits = [
    {
      icon: HiSparkles,
      text: "Sounds like you, never like AI: trained on your own writing, so your book reads as authentically yours - and passes AI detection.",
      highlight: "Sounds like you, never like AI"
    },
    {
      icon: FiShield,
      text: "Publish without rejections: live KDP Preflight and ACX-spec checks catch issues before you upload, not after.",
      highlight: "Publish without rejections"
    },
    {
      icon: FiHeadphones,
      text: "Turn it into an audiobook: pro AI narration in hours, not weeks - ACX-ready for KDP/Virtual Voice, no $2,000 narrator.",
      highlight: "Turn it into an audiobook"
    },
    {
      icon: FiClock,
      text: "Finish faster: outline to full manuscript with AI that continues in your voice, chapter after chapter.",
      highlight: "Finish faster"
    },
    {
      icon: FiAward,
      text: "Export everywhere: EPUB, PDF, DOCX, KDP - and audiobook MP3/M4B - in one click, formatted to spec.",
      highlight: "Export everywhere"
    },
    {
      icon: FiHeart,
      text: "Your words stay yours: private by default, and your voice model is never shared or used to train others.",
      highlight: "Your words stay yours"
    }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Image Background */}
      <div className="absolute inset-0">
        {/* Base image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=1920&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        ></div>
        
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/50 to-gray-900/60"></div>
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          {/* Main Headline */}
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Write like{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">
              yourself.
            </span>{' '}
            Publish without the rejections.
          </motion.h1>

          {/* Audio Wave with Logo Elements */}
          <motion.div 
            className="flex items-center justify-center mb-8 relative"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="relative h-24 w-full max-w-md">
              {/* Animated Audio Wave Background */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-16 w-full flex items-center justify-center space-x-1 overflow-hidden">
                  {[...Array(40)].map((_, i) => (
                    <motion.div 
                      key={`wave-bar-${i}`}
                      className="w-1.5 bg-gradient-to-t from-primary-400 to-secondary-400 rounded-full"
                      style={{ 
                        height: `${Math.sin((i + Date.now() / 1000) * 0.5) * 30 + 30}%`,
                        opacity: i % 3 === 0 ? 0.9 : 0.6,
                      }}
                      animate={{
                        height: [`${Math.sin(i * 0.2) * 30 + 20}%`, `${Math.sin(i * 0.2 + Math.PI) * 30 + 20}%`],
                      }}
                      transition={{
                        repeat: Infinity,
                        repeatType: "reverse",
                        duration: 1 + (i % 4) * 0.2,
                      }}
                    ></motion.div>
                  ))}
                </div>
              </div>

              {/* Icons positioned on top of the wave */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex items-center space-x-6">
                  {/* Pen icon in blue (for "Write") */}
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-primary-400 rounded-full flex items-center justify-center shadow-lg">
                      <SafeIcon icon={FiEdit3} className="w-6 h-6 text-white" />
                    </div>
                    <div className="mt-1 text-xs font-semibold text-primary-300 text-center">Write</div>
                  </div>

                  {/* Headphones icon (for "Narrate") — the audiobook step */}
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-full flex items-center justify-center shadow-lg">
                      <SafeIcon icon={FiHeadphones} className="w-6 h-6 text-white" />
                    </div>
                    <div className="mt-1 text-xs font-semibold text-amber-200 text-center">Narrate</div>
                  </div>

                  {/* Book icon in purple (for "Publish") */}
                  <div className="relative z-10">
                    <div className="w-12 h-12 bg-gradient-to-r from-secondary-500 to-secondary-400 rounded-full flex items-center justify-center shadow-lg">
                      <SafeIcon icon={BsBook} className="w-6 h-6 text-white" />
                    </div>
                    <div className="mt-1 text-xs font-semibold text-secondary-300 text-center">Publish</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Subheadline */}
          <motion.p
            className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            The <span className="font-semibold text-primary-300">writing studio that learns your voice</span> - then
            helps you write in it. For indie authors, ghostwriters, and small publishers: go from first chapter to a
            KDP-ready manuscript, without juggling five tools or fearing the next formatting rejection - then
            <span className="font-semibold text-secondary-300"> turn that finished book into an ACX-ready audiobook</span>, in a natural voice or your own.
          </motion.p>

          {/* Benefits List */}
          <motion.div
            className="max-w-5xl mx-auto mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-left">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 shadow-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 1.1 + index * 0.1 }}
                  whileHover={{ scale: 1.02, backgroundColor: 'rgba(255,255,255,0.15)' }}
                >
                  <div className="flex-shrink-0 mt-1">
                    <SafeIcon icon={benefit.icon} className="w-5 h-5 text-primary-300" />
                  </div>
                  <p className="text-gray-200 leading-relaxed">
                    <span className="font-semibold text-primary-300">{benefit.highlight}: </span>{' '}
                    {benefit.text.replace(`${benefit.highlight}: `, '')}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Primary CTA */}
          <motion.div
            className="space-y-4 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.8 }}
          >
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-center items-center">
              <a
                href="https://app.manuscriptsmith.com/signup"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold text-lg rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all transform hover:scale-105 shadow-xl"
              >
                <SafeIcon icon={FiEdit3} className="w-5 h-5 mr-2" />
                Get started
              </a>
              <button
                type="button"
                onClick={() => document.getElementById('product-showcase')?.scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center px-8 py-4 bg-white/10 border border-white/30 text-white font-semibold text-lg rounded-lg hover:bg-white/20 transition-all"
              >
                <SafeIcon icon={FiShield} className="w-5 h-5 mr-2" />
                See it prove itself ↓
              </button>
            </div>
            <p className="text-sm text-gray-300">Your words stay yours</p>
          </motion.div>

          {/* Secondary CTA */}
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 2.0 }}
          >
            <Link
              to="/try"
              className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all transform hover:scale-105"
            >
              <SafeIcon icon={FiPlay} className="w-5 h-5 mr-2" />
              Try the demo — write in any author&apos;s voice
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;